/**
 * SelectPropertyEditor
 *
 * Reusable select/dropdown component for property editors
 * SRP: Responsible ONLY for rendering a select dropdown and handling changes
 * Used by: control types, animation effects, validation rules, etc.
 */

import React, { useCallback, useMemo } from 'react';

export interface SelectOption {
  /** Option value */
  value: string | number;

  /** Display label */
  label: string;

  /** Optional description */
  description?: string;

  /** If true, option is disabled */
  disabled?: boolean;
}

export interface SelectPropertyEditorProps {
  /** Display label for the select */
  label: string;

  /** Current selected value */
  value: string | number;

  /** Called when selection changes */
  onChange: (value: string) => void;

  /** Available options */
  options: SelectOption[];

  /** Field is required */
  required?: boolean;

  /** Field is disabled */
  disabled?: boolean;

  /** Allow clearing selection */
  clearable?: boolean;

  /** Placeholder text when no selection */
  placeholder?: string;

  /** Help text shown below select */
  helpText?: string;

  /** Custom CSS class */
  className?: string;
}

/**
 * SelectPropertyEditor component
 * Renders a dropdown for choosing from predefined options
 */
export const SelectPropertyEditor: React.FC<SelectPropertyEditorProps> = ({
  label,
  value,
  onChange,
  options,
  required = false,
  disabled = false,
  clearable = false,
  placeholder = 'Select an option...',
  helpText,
  className = '',
}) => {
  // Memoize handler to prevent unnecessary re-renders
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const newValue = event.currentTarget.value;
      onChange(newValue);
    },
    [onChange],
  );

  const inputId = useMemo(
    () => `select-editor-${label.replace(/\s+/g, '-').toLowerCase()}`,
    [label],
  );

  // Validate that current value is in options
  const isValidValue = useMemo(() => {
    if (value === null || value === undefined || value === '') return true;
    return options.some((opt) => opt.value === value);
  }, [value, options]);

  return (
    <div className={`af-property-editor__group ${className}`}>
      <label htmlFor={inputId} className="af-property-editor__label">
        {label}
        {required && <span className="af-property-editor__required">*</span>}
      </label>

      <select
        id={inputId}
        value={value ?? ''}
        onChange={handleChange}
        disabled={disabled}
        required={required}
        className={`af-property-editor__select ${
          !isValidValue ? 'af-property-editor__select--invalid' : ''
        }`}
        aria-label={label}
        aria-required={required}
        aria-invalid={!isValidValue}
        aria-describedby={helpText ? `${inputId}-help` : undefined}
      >
        {(clearable || !required) && (
          <option value="">
            {placeholder}
          </option>
        )}

        {options.map((option) => (
          <option
            key={`${option.value}`}
            value={option.value}
            disabled={option.disabled}
            title={option.description}
          >
            {option.label}
          </option>
        ))}
      </select>

      {!isValidValue && (
        <div className="af-property-editor__error">
          Value is not in available options
        </div>
      )}

      {helpText && (
        <div id={`${inputId}-help`} className="af-property-editor__help">
          {helpText}
        </div>
      )}
    </div>
  );
};

SelectPropertyEditor.displayName = 'SelectPropertyEditor';
